import { Trans, useTranslation } from '@dagens/frontend-i18n';
import { capitalize, getOrderLineAggregates } from '@dagensmat/core';
import { map } from 'lodash';
import queryString from 'query-string';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '../../../_common/hooks/reduxHooks';
import { OnboardingHistoryKeys } from '../../../types/Producer';
import PackingAndLabelingInfo from './PackingAndLabelingInfo';
import ProducerActions from './ProducerActions';
import ProducerOrderGroup from './ProducerOrderGroup';
import Theme from '@carrot-deprecated/theme';
import Container from '@carrot-deprecated/container';
import Typography from '@carrot-deprecated/typography';
import {
  ButtonAnchor,
  ButtonContainer,
  ButtonLink
} from '_common/components/button/Button.style';
import ExpandableCallout from '_common/components/callout/ExpandableCallout';
import InContextOnboardingMessage from '_common/components/in-context-onboarding-message/InContextOnboardingMessage';
import OverviewTable from '_common/components/order-overview/OverviewTable';
import DeliveryTypeTag from '_common/components/tags/DeliveryTypeTag';
import TextLink from '_common/components/text-link/TextLink.style';
import Tag from '_common/components/utils/Tag';
import {
  getOrderGroup,
  isDeliveredToConsumer,
  splitActiveAndPastOrders
} from '_common/reducers/orders';
import { Page } from '@components/page';
import { baseUrl } from 'config';
import { DeliveryType } from 'types/Logistics';
import { Order } from 'types/Order';
import { formatDate } from 'utils/date/format';
import { hasAllOrdersLeftProducer, PRODUCER_DELIVERS } from 'utils/delivery';
import { DOWNLOAD_DELIVERY_NOTES, track } from 'utils/mixpanel';

const EmptyPage = () => {
  const { t } = useTranslation();
  return (
    <Typography variant="paragraph" color="secondary" my="m">
      {t('producer:NothingLeftToDoWithThisOrder')}
      <TextLink
        to="/orders"
        mt={Theme.Spacings.xs}
        color={Theme.Colours.black}
        align="left"
      >
        {t('producer:BackToMyOrdersWithArrow')}
      </TextLink>
    </Typography>
  );
};

const DeliveredToConsumer = ({
  producerDeliveryDate
}: {
  producerDeliveryDate: string;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <Tag
        bgColor={Theme.Colours.availability}
        text={t('producer:DeliveredToConsumer')}
      />
      <Typography variant="primaryHeading" mt="s">
        {capitalize(formatDate(producerDeliveryDate))}
      </Typography>
      <Typography variant="paragraph" mt="s">
        {t('producer:YouAreDoneWithYourPart')}.
      </Typography>
      <Typography variant="paragraph" mt="s">
        <Trans
          t={t}
          i18nKey="producer:OrderFoundUnderInvoicing"
          components={{
            TypographyLink: (
              <Typography variant="routerLink" to="/orders/invoice" />
            )
          }}
        />
      </Typography>
    </>
  );
};

export type IOrderGroup = {
  key: string;
  deliveryDate: Order['deliveryDate'];
  orders: Order[];
  producerDeliveryDate: string;
  type: string;
  toName?: string;
  toDeadline?: string;
};

const OrdersByDeliveryPage = () => {
  const { onboardingHistory } = useAppSelector(
    ({ auth: { onboardingHistory } }) => ({ onboardingHistory })
  );
  const { t } = useTranslation();
  const { key } = useParams<{ key: string }>();
  const { orderGroup, pastOrderGroup } = useAppSelector(({ orders }) => {
    const { activeOrders, pastOrders } = splitActiveAndPastOrders(orders.items);
    const group = getOrderGroup(activeOrders, key);
    const pastGroup = getOrderGroup(pastOrders, key);
    return {
      orderGroup: group,
      pastOrderGroup: pastGroup
    };
  });
  const isOnboarding =
    !onboardingHistory?.[OnboardingHistoryKeys.deliveredFirstOrder];

  const hasBeenDeliveredToConsumer =
    !orderGroup &&
    pastOrderGroup &&
    pastOrderGroup?.orders &&
    pastOrderGroup?.orders.some(isDeliveredToConsumer);

  if (hasBeenDeliveredToConsumer) {
    return (
      <Page>
        <DeliveredToConsumer
          producerDeliveryDate={pastOrderGroup.producerDeliveryDate}
        />
      </Page>
    );
  }

  const { orders = [], key: pickupKey, type, deliveryDate } = orderGroup || {};

  return (
    <Page>
      {!orderGroup ? (
        <EmptyPage />
      ) : (
        <>
          <DeliveryTypeTag
            deliveryType={
              type === PRODUCER_DELIVERS
                ? DeliveryType.SELF_DELIVERY
                : DeliveryType.DAGENS_DELIVERY
            }
          />
          <Typography variant="primaryHeading" mt="s">
            {capitalize(formatDate(deliveryDate))}
          </Typography>
          <InContextOnboardingMessage
            message={t('producer:orders.getReadyForDelivery')}
            showStep={isOnboarding}
            mt="m"
          />
          <ProducerActions orderGroup={orderGroup} />
          <Container mt="xl" mb="s">
            <Typography variant="secondaryHeading" my="s">
              {t('common:OrderedProducts')}
            </Typography>
            <OverviewTable
              orderLineAggregates={getOrderLineAggregates(orders)}
            />
          </Container>
          <ButtonContainer
            verticalSpacer={Theme.Spacings.xs}
            horizontalSpacer={Theme.Spacings.xxs}
            paddingY={0}
            textAlign="left"
          >
            {!hasAllOrdersLeftProducer(orders) && (
              <ButtonLink
                to={{
                  pathname: `/orders/by-delivery/${pickupKey}/parcels`
                }}
                state={{ returnPath: true }}
                variant="primary"
              >
                {t('common:parcelLabelModal.labelDownload')}
              </ButtonLink>
            )}
            <ButtonAnchor
              onClick={() => {
                track(DOWNLOAD_DELIVERY_NOTES);
              }}
              target="_blank"
              rel="noopener noreferrer"
              href={`${baseUrl}/export/deliveryNote?${queryString.stringify(
                {
                  orderIds: map(orders, '_id')
                },
                { arrayFormat: 'bracket' }
              )}`}
              variant="secondary"
            >
              {t('producer:DownloadDeliveryNotes')}
            </ButtonAnchor>
            <ButtonLink
              to={{
                pathname: `/orders/by-delivery/${pickupKey}/packing-list`
              }}
              state={{ returnPath: true }}
            >
              {t('producer:ViewPackingList')}
            </ButtonLink>
          </ButtonContainer>
          <Container mt="l" mb="m">
            <ProducerOrderGroup
              headerText={t('common:Order_other')}
              orders={orders}
            />
          </Container>
          <ExpandableCallout expandedContent={<PackingAndLabelingInfo />}>
            {t('producer:HowToPackAndLabel')}
          </ExpandableCallout>
        </>
      )}
    </Page>
  );
};

export default OrdersByDeliveryPage;
